import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useEntitySearch } from "../hooks/useEntitySearch";
import { normalizeEntityType } from "./relationships/SectionConfig";

const EntitySelectorModal = ({
  isOpen,
  onClose,
  onSelect,
  currentEntityType,
  currentEntityId,
  title,
  selectedEntityIds = [],
  allowMultiple = false,
}) => {
  const normalizedType = normalizeEntityType(currentEntityType);
  const selectedKey = useMemo(
    () =>
      (Array.isArray(selectedEntityIds) ? selectedEntityIds : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
        .sort()
        .join("|"),
    [selectedEntityIds]
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const {
    results,
    loading,
    error,
    hasMore,
    search,
    loadMore,
    initialLoad,
    reset,
  } = useEntitySearch(normalizedType, {
    enabled: isOpen && Boolean(normalizedType),
    debounceMs: 250,
    pageSize: 50,
    architectureExplorer: false,
  });

  useEffect(() => {
    if (!isOpen || !normalizedType) {
      setSearchTerm("");
      setSelectedIds(new Set());
      reset();
      return;
    }
    setSearchTerm("");
    setSelectedIds(
      new Set(
        selectedKey
          ? selectedKey.split("|").filter(Boolean)
          : []
      )
    );
  }, [isOpen, normalizedType, reset, selectedKey]);

  useEffect(() => {
    if (!isOpen || !normalizedType) return;
    if (!searchTerm) {
      initialLoad();
      return;
    }
    search(searchTerm, true);
  }, [initialLoad, isOpen, normalizedType, search, searchTerm]);

  const filteredEntities = useMemo(() => {
    const excludeId = String(currentEntityId || "");
    return (Array.isArray(results) ? results : []).filter(
      (entity) => String(entity?.id || "") !== excludeId
    );
  }, [currentEntityId, results]);

  const handleToggleSelection = (entityId) => {
    const id = String(entityId || "");
    if (!id) return;
    if (!allowMultiple) {
      setSelectedIds(new Set([id]));
      return;
    }
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleConfirm = () => {
    onSelect(Array.from(selectedIds));
    onClose();
  };

  const handleCancel = () => {
    setSelectedIds(
      new Set(selectedKey ? selectedKey.split("|").filter(Boolean) : [])
    );
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/55 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="entity-selector-title"
    >
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-gray-800 shadow-xl">
        <div className="border-b border-gray-700 px-6 py-4">
          <h2
            id="entity-selector-title"
            className="text-xl font-semibold text-white"
          >
            {title}
          </h2>
          <p className="mt-1 text-sm text-gray-400">
            Select {allowMultiple ? "one or more" : "one"} entity of type{" "}
            <span className="font-medium">
              {normalizedType || currentEntityType || "unknown"}
            </span>
          </p>
        </div>

        <div className="border-b border-gray-700 px-6 py-4">
          <input
            type="text"
            placeholder="Search entities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-gray-600 bg-gray-700 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && filteredEntities.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500" />
              <span className="ml-3 text-gray-400">Loading entities...</span>
            </div>
          ) : error ? (
            <div className="py-8 text-center text-sm text-red-300">{error}</div>
          ) : filteredEntities.length === 0 ? (
            <div className="py-8 text-center text-gray-400">
              {searchTerm
                ? "No entities found matching your search."
                : "No entities of this type available."}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredEntities.map((entity) => {
                const id = String(entity.id || "");
                const isSelected = selectedIds.has(id);
                return (
                  <div
                    key={id}
                    onClick={() => handleToggleSelection(id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleToggleSelection(id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                      isSelected
                        ? "border-blue-500 bg-blue-600"
                        : "border-gray-600 bg-gray-700 hover:bg-gray-650"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-white">
                            {entity.name}
                          </div>
                          {isSelected ? (
                            <span className="rounded bg-blue-500 px-2 py-0.5 text-xs text-white">
                              Selected
                            </span>
                          ) : null}
                        </div>
                        {entity.description ? (
                          <div className="mt-1 line-clamp-2 text-sm text-gray-400">
                            {entity.description}
                          </div>
                        ) : null}
                      </div>
                      {isSelected ? (
                        <div className="ml-3 flex-shrink-0 text-blue-300">
                          <svg
                            className="h-5 w-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            aria-hidden
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
              {hasMore ? (
                <button
                  type="button"
                  onClick={() => loadMore()}
                  disabled={loading}
                  className="mt-2 w-full rounded-lg border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-gray-200 hover:bg-gray-600 disabled:opacity-50"
                >
                  {loading ? "Loading more…" : "Load more"}
                </button>
              ) : null}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-700 px-6 py-4">
          <button
            type="button"
            onClick={handleCancel}
            className="rounded-lg bg-gray-700 px-4 py-2 text-white transition-colors hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={selectedIds.size === 0}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Select {selectedIds.size > 0 ? `(${selectedIds.size})` : ""}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EntitySelectorModal;
